import { useCallback, useEffect, useState } from 'react';
import { Confirm, useNotify, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import * as sessionService from '../../services/sessionService';
import { SESSION_PAGE_SIZE, SessionTable } from './SessionTable';

interface MySessionsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface PendingAction {
  content: string;
  run: () => Promise<void>;
}

// XBIT-4828 D9: the caller's own sessions. Ending the session of this browser is not offered (Logout does that).
export function MySessionsDialog({ open, onClose }: MySessionsDialogProps) {
  const t = useTranslate();
  const notify = useNotify();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<sessionService.SessionView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [acting, setActing] = useState(false);

  const fail = useCallback(
    (error: any) => {
      notify(error?.response?.data?.message || t('module.session.failed'), { type: 'error' });
    },
    [notify, t]
  );

  const load = useCallback(
    async (target: number) => {
      setLoading(true);
      try {
        const res = await sessionService.listMySessions(target, SESSION_PAGE_SIZE);
        setRows(res.data.data);
        setTotal(res.data.total);
      } catch (error: any) {
        fail(error);
      } finally {
        setLoading(false);
      }
    },
    [fail]
  );

  useEffect(() => {
    if (open) load(page);
  }, [open, page, load]);

  useEffect(() => {
    if (!open) setPage(1);
  }, [open]);

  const confirmed = async () => {
    if (!pending) return;
    setActing(true);
    try {
      await pending.run();
      await load(page);
    } catch (error: any) {
      fail(error);
    } finally {
      setActing(false);
      setPending(null);
    }
  };

  const askEnd = (sid: string) =>
    setPending({
      content: t('module.session.confirmEnd'),
      run: async () => {
        await sessionService.revokeMySession(sid);
        notify(t('module.session.ended'), { type: 'success' });
      },
    });

  const askEndOthers = () =>
    setPending({
      content: t('module.session.confirmEndOthers'),
      run: async () => {
        const res = await sessionService.revokeMyOtherSessions();
        notify(t('module.session.endedMany', { count: res.data.revokedSessionIds.length }), {
          type: 'success',
        });
      },
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('module.session.mySessions')}</DialogTitle>
      <DialogContent>
        <SessionTable
          rows={rows}
          total={total}
          page={page}
          onPageChange={setPage}
          loading={loading}
          canEnd
          onEnd={askEnd}
        />
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={askEndOthers}>
          {t('module.session.endOthers')}
        </Button>
        <Button onClick={onClose}>{t('ra.action.close')}</Button>
      </DialogActions>
      <Confirm
        isOpen={Boolean(pending)}
        loading={acting}
        title={t('module.session.mySessions')}
        content={pending?.content ?? ''}
        onConfirm={confirmed}
        onClose={() => setPending(null)}
      />
    </Dialog>
  );
}
